/**
 * MergedCreateActionCard — renders a create action with its absorbed
 * association actions as nested entity cards.
 *
 * The primary entity's properties are rendered at the top via the
 * ENTITY_CONTENT_REGISTRY (e.g. DealCardContent for deals) or
 * EntityCard for types without a registered content component.
 *
 * Each merged association (contact, company, etc.) is rendered as a
 * bordered ObjectCard below using the entity content registry — so
 * every associated entity gets its full card treatment.
 *
 * Association target entities are rendered by AssociationEntityCard →
 * ActionEntityPreview → useCrmObjectSnapshot (which consults the CRM
 * store and the preview overlay), so they show real-time data instead
 * of empty metadata rows. The primary content component self-hydrates
 * from the store via its own entityId/orgId only for entity types in
 * STORE_HYDRATED_PRIMARY_TYPES (currently just contacts) — see that
 * constant for why other types are excluded.
 */

import { useMemo } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { CrmClickableContent } from '@/lib/crm/object-cards/CrmClickableContent';
import { isMemoEntityType } from '@/lib/crm/record/notes/notes.utils';
import { cx } from '@/utils/cx';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
  SourceContext,
} from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import { useProposalSelectionOptional } from '../../lib/proposals/hooks/ProposalSelectionContext';
import type { MergedAction } from '../../lib/proposals/utils/actionMerging';
import { useActionEdit } from './ActionAccordion';
import {
  APPLIES,
  type AssociationExclusion,
  computeVisibleAssociations,
  resolveEnrichedDataForDisplay,
  UNCHECKED,
} from './associationDisposition';
import { AssociationEntityCard } from './AssociationEntityCard';
import {
  ENTITY_CONTENT_REGISTRY,
  extractProperties,
} from './CreateActionCard.helpers';
import { GenericEntityContent } from './GenericEntityContent';
import { MergedCreateEditForm } from './MergedCreateEditForm';
import { NoteCreateActionCard } from './NoteCreateActionCard';
import { ActionError, computeObjectLabel } from './shared';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';

// Primary create-card entity types whose registered content component renders
// associated entities INLINE (e.g. ContactCardContent's "Works at" row) rather
// than as a nested sub-card. Only these get the primary entityId/orgId so their
// store leg resolves the projected association. Types that render a nested
// company sub-card (e.g. DealCardContent) are deliberately excluded — passing
// entityId there would duplicate the company AssociationEntityCard rendered
// below. Mirrors HYDRATED_TARGET_TYPES / ASSOCIATIONS_RENDERED_INLINE_BY_PRIMARY_TYPE
// (in associationDisposition.ts); widen only alongside a content component
// verified not to duplicate a merged association.
const STORE_HYDRATED_PRIMARY_TYPES = new Set<string>(['ahoy_contact']);

// ─── Component ────────────────────────────────────────────────────────────────

export interface MergedCreateActionCardProps {
  merged: MergedAction;
  orgId: number;
  proposalId?: number;
  onUpdate?: (updatedAction: ProposedAction) => void;
  /** Proposal source type (e.g., "ahoy_background_agent") */
  sourceTypeName?: string;
  /** Proposal source context (channel, counterparty, etc.) */
  sourceContext?: SourceContext | null;
}

/**
 * Renders a merged create card with nested association entity cards.
 *
 * Uses the ENTITY_CONTENT_REGISTRY for the primary entity (deals get
 * DealCardContent, contacts get ContactCardContent, etc.) and falls
 * back to EntityCard for entity types without a registered component.
 *
 * Shell (title, checkbox, edit button) is provided by the parent ActionAccordion.
 */
export function MergedCreateActionCard({
  merged,
  orgId,
  proposalId,
  onUpdate,
}: MergedCreateActionCardProps) {
  const { primaryAction, mergedAssociations } = merged;
  const { isEditing } = useActionEdit();

  const flatProps = useMemo(() => {
    if (!isObjectCreatedPayload(primaryAction.payload)) {
      return {};
    }
    return extractProperties(primaryAction.payload.payload.properties);
  }, [primaryAction.payload]);

  const baseEnrichedData = primaryAction.enriched_data ?? undefined;

  const entityTypeName =
    baseEnrichedData?.entity_type_name ??
    (isObjectCreatedPayload(primaryAction.payload)
      ? primaryAction.payload.entity_type_name
      : '');

  const enrichedData = useMemo(
    () =>
      resolveEnrichedDataForDisplay(
        baseEnrichedData,
        entityTypeName,
        mergedAssociations
      ),
    [baseEnrichedData, mergedAssociations, entityTypeName]
  );

  const selection = useProposalSelectionOptional();
  const primaryEntityId = isObjectCreatedPayload(primaryAction.payload)
    ? primaryAction.payload.entity_id
    : undefined;
  const siblingActions = selection?.actions;
  // Entity ids of every OTHER create action on the proposal — each renders its
  // own top-level card, so its target is already "present" and shouldn't be
  // re-rendered as a nested card here. Selection-independent (reads `actions`,
  // not selected ids): an unchecked create card is still on screen. Empty when
  // there's no ProposalSelectionProvider (fail-open → today's full cards).
  const topLevelCreatedIds = useMemo(() => {
    const ids = new Set<string>();
    if (!siblingActions) {
      return ids;
    }
    for (const a of siblingActions) {
      if (
        isObjectCreatedPayload(a.payload) &&
        a.payload.entity_id !== primaryEntityId
      ) {
        ids.add(a.payload.entity_id);
      }
    }
    return ids;
  }, [siblingActions, primaryEntityId]);

  const entityType = enrichedData?.entity_type_name ?? entityTypeName;
  // Absorbed associations have no checkbox of their own, yet their action can
  // still fail to apply: unchecked directly, or invalid because an endpoint's
  // create was unchecked (projection validity — see `actionValidity.ts`). For
  // the invalid case the chip carries the verdict's own sentence, which names
  // the missing entities — possibly the SOURCE (this very card's create), not
  // the target, so the chip must not guess. `isSelected` and
  // `getDisabledReason` change identity on every toggle, which is exactly
  // when the disposition below must be recomputed.
  const isActionSelected = selection?.selectionState.isSelected;
  const describeInvalidity = selection?.describeInvalidity;
  const exclusionFor = useMemo(
    () =>
      isActionSelected && describeInvalidity
        ? (actionId: number): AssociationExclusion => {
            const clause = describeInvalidity(actionId);
            if (clause !== null) {
              return {
                kind: 'invalid',
                reason: `Won't be linked – ${clause}.`,
              };
            }
            return isActionSelected(actionId) ? APPLIES : UNCHECKED;
          }
        : undefined,
    [isActionSelected, describeInvalidity]
  );
  // Association cards for read mode (edit mode renders editable targets via
  // MergedCreateEditForm instead).
  const visibleAssociations = useMemo(
    () =>
      computeVisibleAssociations(
        mergedAssociations,
        topLevelCreatedIds,
        entityType,
        exclusionFor
      ),
    [mergedAssociations, topLevelCreatedIds, entityType, exclusionFor]
  );

  if (!isObjectCreatedPayload(primaryAction.payload)) {
    return <ActionError message='Invalid merged action payload' />;
  }

  // Notes and tasks render via NoteCreateActionCard (the canonical NoteCard)
  // rather than the generic entity-card + association-cards path. Owner and
  // stage are folded INTO the note card (footer + stage badge); the other
  // absorbed associations (creator, visibility, parent link) are intentionally
  // hidden. Tasks carry the same family of edges under `ahoy_task_*` names.
  if (isMemoEntityType(entityTypeName) && orgId) {
    const noteAction =
      primaryAction as ProposedAction<ObjectCreatedEntityPayload>;
    return (
      <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-3')}>
        <NoteCreateActionCard
          action={noteAction}
          proposalId={proposalId}
          orgId={orgId}
          onUpdate={onUpdate}
          associations={mergedAssociations}
        />
        {noteAction.payload.context_message && (
          <MarkdownContent
            className='text-sm text-fg-secondary'
            content={noteAction.payload.context_message}
            orgId={orgId}
            size='compact'
            resolvedSlots={noteAction.enriched_data?.resolved_slots}
          />
        )}
      </div>
    );
  }

  const typedAction =
    primaryAction as ProposedAction<ObjectCreatedEntityPayload>;

  // Look up the entity-specific content component from the registry
  const ContentComponent = ENTITY_CONTENT_REGISTRY[entityType];
  const primaryCrmEntityId = typedAction.payload.entity_id;

  const associationCards =
    visibleAssociations.length > 0 ? (
      <div className='space-y-2'>
        {visibleAssociations.map(({ assoc, compact, exclusion }) => (
          <AssociationEntityCard
            key={assoc.action.id}
            assoc={assoc}
            orgId={orgId}
            sourceEntityType={entityType}
            compact={compact}
            excluded={exclusion.kind !== 'applies'}
            excludedMessage={
              exclusion.kind === 'invalid' ? exclusion.reason : undefined
            }
          />
        ))}
      </div>
    ) : null;

  // Edit mode — editable primary fields + editable association targets,
  // committed under one combined save (owned by MergedCreateEditForm).
  if (isEditing) {
    return (
      <MergedCreateEditForm
        primaryAction={typedAction}
        mergedAssociations={mergedAssociations}
        orgId={orgId}
        proposalId={proposalId}
        onUpdate={onUpdate}
      />
    );
  }

  // Read mode — primary entity content + nested association cards
  return (
    <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-3')}>
      {ContentComponent ? (
        <CrmClickableContent entityId={primaryCrmEntityId}>
          <ContentComponent
            flatProps={flatProps}
            enrichedData={enrichedData}
            orgId={orgId}
            {...(STORE_HYDRATED_PRIMARY_TYPES.has(entityType)
              ? { entityId: primaryCrmEntityId }
              : {})}
          />
        </CrmClickableContent>
      ) : (
        // Custom / unregistered entity types get the schema-driven generic
        // card (display name + top fields) instead of a bare icon+label.
        <CrmClickableContent entityId={primaryCrmEntityId}>
          <GenericEntityContent
            entityType={entityType}
            // computeObjectLabel first (it resolves the real name from the
            // payload, e.g. a custom object's ahoy_display_name) — enriched_data
            // object_label for a custom create is only the generic type label
            // ("New Org Bug"). Matches CreateActionCard's precedence.
            label={
              computeObjectLabel(flatProps, entityType) ||
              enrichedData?.object_label ||
              `New ${entityType.replace(/^ahoy_/, '')}`
            }
            flatProps={flatProps}
            orgId={orgId}
          />
        </CrmClickableContent>
      )}
      {typedAction.payload.context_message && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={typedAction.payload.context_message}
          orgId={orgId}
          size='compact'
          resolvedSlots={typedAction.enriched_data?.resolved_slots}
        />
      )}
      {associationCards}
    </div>
  );
}
